<script lang="ts">
	import { SvelteMap, SvelteSet } from 'svelte/reactivity';
	import { Download, LoaderCircle } from '@lucide/svelte';
	import { getModeName } from '$lib/getModeName';
	import { lngLatToStr } from '$lib/lngLatToStr';
	import GeoJSON from '$lib/map/GeoJSON.svelte';
	import Layer from '$lib/map/Layer.svelte';
	import Popup from '$lib/map/Popup.svelte';
	import polyline from '@mapbox/polyline';
	import { colord } from 'colord';
	import type { Position } from 'geojson';
	import maplibregl from 'maplibre-gl';
	import type { FeatureCollection, LineString, Point } from 'geojson';
	import type { Leg, RouteInfo, RoutePolyline, RoutesResponse, RoutesError } from '@motis-project/motis-client';
	import { getDecorativeColors } from '$lib/map/colors';
	import { t } from '$lib/i18n/translation';
	import { client } from '@motis-project/motis-client';

	let {
		map,
		bounds,
		zoom,
		shapesDebugEnabled = false
	}: {
		map: maplibregl.Map | undefined;
		bounds: maplibregl.LngLatBoundsLike | undefined;
		zoom: number;
		shapesDebugEnabled?: boolean;
	} = $props();

	const FETCH_PADDING_RATIO = 0.5;

	type RoutesPayload = RoutesResponse;

	let routesData = $state<RoutesPayload | null>(null);
	let loadedBounds = $state<maplibregl.LngLatBounds | null>(null);
	let loadedZoom = $state<number | null>(null);
	let requestToken = 0;
	let hoveredArrayIdx = $state<number | null>(null);
	let downloadingRouteIdx = $state<number | null>(null);

	const stringToHash = (str: string) => {
		let hash = 0;
		for (let i = 0; i < str.length; i++) {
			hash = str.charCodeAt(i) + ((hash << 5) - hash);
		}
		return hash;
	};

	const getRouteColor = (name: string) => {
		const hash = stringToHash(name);
		const h = Math.abs(hash % 360);
		return colord({ h, s: 80, l: 60 }).toHex();
	};

	type RouteFeatureProperties = {
		color: string;
		name: string;
		routeIndexes: string; // comma-separated because MapLibre stringifies properties
	};

	const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

	const getRouteDisplayProps = (route: RouteInfo | undefined) => {
		if (!route) {
			return { name: '', color: '#000000' };
		}
		const shortNames = Array.from(new Set(route.transitRoutes.map((r) => r.shortName)));
		const name = shortNames.join(', ');
		const apiColor = route.transitRoutes.find((r) => r.color)?.color;
		const color = apiColor ? `#${apiColor}` : getRouteColor(name);
		return { name, color };
	};

	const getPolylineDisplayProps = (routePolyline: RoutePolyline, routeList: RouteInfo[]) => {
		const rdp = getRouteDisplayProps(routeList[routePolyline.routeIndexes[0]]);
		const color = routePolyline.colors.length ? `#${routePolyline.colors[0]}` : rdp.color;
		return { name: rdp.name, color };
	};

	const expandBounds = (value: maplibregl.LngLatBounds) => {
		const sw = value.getSouthWest();
		const ne = value.getNorthEast();
		const padLng = (ne.lng - sw.lng) * FETCH_PADDING_RATIO;
		const padLat = (ne.lat - sw.lat) * FETCH_PADDING_RATIO;
		return new maplibregl.LngLatBounds(
			[clamp(sw.lng - padLng, -180, 180), clamp(sw.lat - padLat, -90, 90)],
			[clamp(ne.lng + padLng, -180, 180), clamp(ne.lat + padLat, -90, 90)]
		);
	};

	const boundsContain = (outer: maplibregl.LngLatBounds | null, inner: maplibregl.LngLatBounds) =>
		!!outer && outer.contains(inner.getSouthWest()) && outer.contains(inner.getNorthEast());

	const fetchRoutes = async (mapBounds: maplibregl.LngLatBounds) => {
		const expandedBounds = expandBounds(mapBounds);
		const isBoundsContained = loadedBounds && boundsContain(loadedBounds, mapBounds);
		const isZoomSufficient =
			!routesData?.zoomFiltered || Math.floor(zoom) <= Math.floor(loadedZoom ?? 0);

		if (isBoundsContained && isZoomSufficient) {
			return;
		}

		const token = ++requestToken;

		const requestWithBounds = async (requestBounds: maplibregl.LngLatBounds) => {
			const max = lngLatToStr(requestBounds.getNorthWest());
			const min = lngLatToStr(requestBounds.getSouthEast());
			console.debug('[Routes] requesting routes', { min, max, zoom });
			return {
				...(await client.get<RoutesResponse, RoutesError>({
					url: '/api/experimental/map/routes',
					query: { max, min, zoom }
				})),
				requestBounds
			};
		};

		let { data, error, response, requestBounds } = await requestWithBounds(expandedBounds);

		if (token !== requestToken) {
			return;
		}

		if (error && response?.status === 422) {
			console.debug(
				'[Routes] routes request returned 422 for expanded bounds, retrying with map bounds'
			);
			({ data, error, response, requestBounds } = await requestWithBounds(mapBounds));
			if (token !== requestToken) {
				return;
			}
		}

		if (error || !data) {
			console.error('[Routes] routes request failed', error);
			return;
		}
		console.debug(
			`[Routes] received ${data.routes.length} routes, ${data.polylines.length} polylines, ${data.stops.length} stops, zoomFiltered=${data.zoomFiltered}`
		);

		routesData = data;
		loadedBounds = requestBounds;
		loadedZoom = zoom;
	};

	$effect(() => {
		if (map && bounds) {
			const b = maplibregl.LngLatBounds.convert(bounds);
			fetchRoutes(b);
		}
	});

	let decodedPolylines = $derived.by(() =>
		routesData
			? routesData.polylines.map((segment) =>
					polyline
						.decode(segment.polyline.points, segment.polyline.precision)
						.map(([lat, lng]) => [lng, lat] as Position)
				)
			: []
	);

	let routeFeatures = $derived.by((): FeatureCollection<LineString> => {
		if (!routesData) {
			return { type: 'FeatureCollection', features: [] };
		}
		const data = routesData;
		return {
			type: 'FeatureCollection',
			features: data.polylines.map((segment, polylineIdx) => {
				const pdp = getPolylineDisplayProps(segment, data.routes);
				return {
					type: 'Feature',
					geometry: {
						type: 'LineString',
						coordinates: decodedPolylines[polylineIdx] ?? []
					},
					properties: {
						color: pdp.color,
						name: pdp.name,
						routeIndexes: segment.routeIndexes.join(',')
					}
				};
			})
		};
	});

	let hoverRouteFeatures = $derived.by((): FeatureCollection<LineString> => {
		if (!routesData || hoveredArrayIdx === null) {
			return { type: 'FeatureCollection', features: [] };
		}
		const route = routesData.routes[hoveredArrayIdx];
		if (!route) {
			return { type: 'FeatureCollection', features: [] };
		}
		const { name, color } = getRouteDisplayProps(route);
		const { outlineColor, chevronColor } = getDecorativeColors(color);
		return {
			type: 'FeatureCollection',
			features: route.segments.map((segment) => ({
				type: 'Feature',
				geometry: {
					type: 'LineString',
					coordinates: decodedPolylines[segment.polyline] ?? []
				},
				properties: {
					color,
					outlineColor,
					chevronColor,
					name,
					arrayIdx: hoveredArrayIdx
				}
			}))
		};
	});

	let hoverStopFeatures = $derived.by((): FeatureCollection<Point> => {
		if (!routesData || hoveredArrayIdx === null) {
			return { type: 'FeatureCollection', features: [] };
		}
		const data = routesData;
		const route = data.routes[hoveredArrayIdx];
		if (!route) {
			return { type: 'FeatureCollection', features: [] };
		}

		const stopMap = new SvelteMap<
			string,
			{ lat: number; lon: number; name: string; color: string }
		>();
		const { color } = getRouteDisplayProps(route);

		route.segments.forEach((segment) => {
			[segment.from, segment.to].forEach((stopIdx) => {
				const stop = data.stops[stopIdx];
				if (!stop) {
					return;
				}
				const stopId = stop.stopId || `${stop.lat},${stop.lon}`;
				if (!stopMap.has(stopId)) {
					stopMap.set(stopId, {
						lat: stop.lat,
						lon: stop.lon,
						name: stop.name,
						color
					});
				}
			});
		});

		return {
			type: 'FeatureCollection',
			features: Array.from(stopMap.values()).map((stop) => ({
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates: [stop.lon, stop.lat]
				},
				properties: {
					name: stop.name,
					color: stop.color
				}
			}))
		};
	});

	const getRouteModeName = (mode: RouteInfo['mode']) => getModeName({ mode } as Leg);

	const getRouteFeaturesAtPoint = (
		event: maplibregl.MapMouseEvent,
		features?: maplibregl.MapGeoJSONFeature[]
	) => {
		const queried = map?.queryRenderedFeatures(event.point, { layers: ['routes-layer'] });
		return (queried?.length ? queried : (features ?? [])) as maplibregl.MapGeoJSONFeature[];
	};

	const getRoutesFromFeatures = (features: maplibregl.MapGeoJSONFeature[]) => {
		const rd = routesData?.routes;
		if (!rd || !rd.length) {
			return [] as Array<{ route: RouteInfo; arrayIdx: number; color: string }>;
		}
		const indexes = new SvelteSet<number>();
		for (const feature of features) {
			const props = feature.properties as RouteFeatureProperties | null;
			const routeIndexes = (props?.routeIndexes ?? '').split(',').map((s) => Number.parseInt(s));
			routeIndexes.forEach((idx) => {
				if (idx >= 0 && idx < rd.length) {
					indexes.add(idx);
				}
			});
		}
		return Array.from(indexes)
			.map((arrayIdx) => {
				const route = rd[arrayIdx];
				return {
					route,
					arrayIdx,
					color: route ? getRouteDisplayProps(route).color : ''
				};
			})
			.filter(
				(entry): entry is { route: RouteInfo; arrayIdx: number; color: string } => !!entry.route
			)
			.sort((a, b) => a.route.routeIdx - b.route.routeIdx);
	};

	function getUrlBase(url: string): string {
		const { origin, pathname } = new URL(url);
		return origin + pathname.slice(0, pathname.lastIndexOf('/') + 1);
	}

	const getDownloadFilename = (contentDisposition: string | null, fallback: string): string => {
		if (!contentDisposition) {
			return fallback;
		}

		const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
		if (utf8Match?.[1]) {
			try {
				return decodeURIComponent(utf8Match[1]).trim();
			} catch {} // eslint-disable-line
		}

		const plainMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
		return plainMatch?.[1]?.trim() || fallback;
	};

	const downloadRouteDebug = async (routeIdx: number) => {
		if (downloadingRouteIdx === routeIdx) {
			return;
		}

		downloadingRouteIdx = routeIdx;
		try {
			const apiBaseUrl = getUrlBase(
				client.getConfig().baseUrl
					? client.getConfig().baseUrl + '/'
					: window.location.origin + window.location.pathname
			);
			const response = await fetch(`${apiBaseUrl}api/experimental/shapes-debug/${routeIdx}`);
			if (!response.ok) {
				const body = await response.text();
				throw new Error(body || `HTTP ${response.status}`);
			}

			const blob = await response.blob();
			const filename = getDownloadFilename(
				response.headers.get('content-disposition'),
				`r_${routeIdx}.json.gz`
			);

			const objectUrl = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = objectUrl;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			a.remove();
			URL.revokeObjectURL(objectUrl);
		} catch (error) {
			console.error('[Routes] failed to download shapes debug data', error);
		} finally {
			downloadingRouteIdx = null;
		}
	};
</script>

{#snippet routesPopup(
	event: maplibregl.MapMouseEvent,
	_2: () => void,
	features: maplibregl.MapGeoJSONFeature[] | undefined
)}
	{@const routeFeaturesAtPoint = getRouteFeaturesAtPoint(event, features)}
	{@const routesAtPoint = getRoutesFromFeatures(routeFeaturesAtPoint)}
	<div
		class="min-w-[340px] max-w-[750px] max-h-[480px] overflow-y-auto pr-1 text-sm"
		role="dialog"
		tabindex="0"
		onmouseleave={() => {
			hoveredArrayIdx = null;
		}}
	>
		<div class="font-semibold mb-2">{t.routes(routesAtPoint.length)}</div>
		<table class="w-full text-sm border-separate border-spacing-y-1">
			<thead class="text-xs uppercase text-muted-foreground">
				<tr>
					<th class="w-4 px-1 py-1"></th>
					<th class="px-1 py-1 text-left font-medium">Index</th>
					<th class="px-1 py-1 text-left font-medium">Mode</th>
					<th class="px-1 py-1 text-left font-medium">ID</th>
					<th class="px-1 py-1 text-left font-medium">Name</th>
					<th class="px-1 py-1 text-left font-medium">Stops</th>
					<th class="px-1 py-1 text-left font-medium">Source</th>
					{#if shapesDebugEnabled}
						<th class="px-1 py-1 text-left font-medium">Debug Data</th>
					{/if}
				</tr>
			</thead>
			<tbody>
				{#each routesAtPoint as entry (entry.arrayIdx)}
					<tr
						class="group align-middle hover:bg-muted/80"
						onmouseenter={() => {
							hoveredArrayIdx = entry.arrayIdx;
						}}
						onmouseleave={() => {
							hoveredArrayIdx = null;
						}}
					>
						<td class="px-1 py-1">
							<span class="inline-block h-2.5 w-2.5 rounded-full" style="background: {entry.color}"
							></span>
						</td>
						<td class="px-1 py-1 pr-2">{entry.route.routeIdx}</td>
						<td class="px-1 py-1 pr-2">{getRouteModeName(entry.route.mode)}</td>
						<td class="px-1 py-1 pr-2 whitespace-nowrap">
							{#if entry.route.transitRoutes.length}
								{#each entry.route.transitRoutes as tr, i (tr.id + i)}
									{tr.id}{#if i < entry.route.transitRoutes.length - 1}<br />{/if}
								{/each}
							{:else}
								<span class="text-muted-foreground">—</span>
							{/if}
						</td>
						<td class="px-1 py-1 pr-1">
							{#if entry.route.transitRoutes.length}
								{#each entry.route.transitRoutes as tr, i (tr.id + i)}
									{tr.shortName}{#if i < entry.route.transitRoutes.length - 1}<br />{/if}
								{/each}
							{:else}
								<span class="text-muted-foreground">—</span>
							{/if}
						</td>
						<td class="px-1 py-1 pr-1">{entry.route.numStops}</td>
						<td class="px-1 py-1 pr-2">{entry.route.pathSource}</td>
						{#if shapesDebugEnabled}
							<td class="px-1 py-1">
								<button
									type="button"
									class="inline-flex h-6 min-w-[7.6rem] items-center justify-center gap-1 rounded border border-border/80 bg-background/85 px-2 text-[11px] leading-none text-foreground transition-colors hover:bg-background disabled:cursor-wait disabled:opacity-70"
									disabled={downloadingRouteIdx === entry.route.routeIdx}
									aria-busy={downloadingRouteIdx === entry.route.routeIdx}
									onclick={(event) => {
										event.stopPropagation();
										void downloadRouteDebug(entry.route.routeIdx);
									}}
								>
									{#if downloadingRouteIdx === entry.route.routeIdx}
										<LoaderCircle class="h-3.5 w-3.5 animate-spin" />
										<span>Generating...</span>
									{:else}
										<Download class="h-3.5 w-3.5" />
										<span>Debug</span>
									{/if}
								</button>
							</td>
						{/if}
					</tr>
				{/each}
			</tbody>
		</table>
	</div>
{/snippet}

<GeoJSON id="routes-source" data={routeFeatures}>
	<Layer
		id="routes-layer"
		type="line"
		filter={['all']}
		paint={{
			'line-color': ['get', 'color'],
			'line-width': 3,
			'line-opacity': 0.7
		}}
		layout={{
			'line-cap': 'round',
			'line-join': 'round'
		}}
	>
		<Popup trigger="click" children={routesPopup} />
	</Layer>
	<Layer
		id="routes-names"
		type="symbol"
		filter={['all']}
		layout={{
			'symbol-placement': 'line',
			'text-field': ['get', 'name'],
			'text-font': ['Noto Sans Regular'],
			'text-size': 12,
			'text-max-angle': 30
		}}
		paint={{
			'text-color': '#000000',
			'text-halo-color': '#ffffff',
			'text-halo-width': 2
		}}
	/>
</GeoJSON>

<GeoJSON id="routes-hover-source" data={hoverRouteFeatures}>
	<Layer
		id="routes-hover-outline"
		type="line"
		filter={['all']}
		beforeLayerId="routes-names"
		paint={{
			'line-color': '#ffffff',
			'line-width': 20,
			'line-opacity': 0.9
		}}
		layout={{
			'line-cap': 'round',
			'line-join': 'round'
		}}
	/>
	<Layer
		id="routes-hover"
		type="line"
		filter={['all']}
		beforeLayerId="routes-names"
		paint={{
			'line-color': ['get', 'color'],
			'line-width': 14,
			'line-opacity': 0.95
		}}
		layout={{
			'line-cap': 'round',
			'line-join': 'round'
		}}
	/>
	<Layer
		id="routes-hover-chevrons"
		type="symbol"
		filter={['all']}
		beforeLayerId="routes-names"
		layout={{
			'symbol-placement': 'line',
			'symbol-spacing': 40,
			'text-field': '›',
			'text-size': 24,
			'text-font': ['Noto Sans Bold'],
			'text-keep-upright': false,
			'text-allow-overlap': true,
			'text-rotation-alignment': 'map',
			'text-offset': [0, -0.1]
		}}
		paint={{
			'text-color': ['get', 'chevronColor'],
			'text-opacity': 0.85,
			'text-halo-color': ['get', 'outlineColor'],
			'text-halo-width': 0.5,
			'text-halo-blur': 0.2
		}}
	/>
</GeoJSON>

<GeoJSON id="routes-hover-stops-source" data={hoverStopFeatures}>
	<Layer
		id="routes-hover-stops"
		type="circle"
		layout={{}}
		filter={['all']}
		paint={{
			'circle-radius': 5,
			'circle-color': 'white',
			'circle-stroke-width': 4,
			'circle-stroke-color': ['get', 'color']
		}}
	/>
	<Layer
		id="routes-hover-stops-names"
		type="symbol"
		filter={['all']}
		layout={{
			'text-field': ['get', 'name'],
			'text-font': ['Noto Sans Regular'],
			'text-size': 12,
			'text-offset': [0, 1],
			'text-anchor': 'top'
		}}
		paint={{
			'text-color': '#000000',
			'text-halo-color': '#ffffff',
			'text-halo-width': 2
		}}
	/>
</GeoJSON>
